import { type RefObject, useEffect, useRef, useState } from "react";

export interface UseCameraResult {
  videoRef: RefObject<HTMLVideoElement | null>;
  ready: boolean;
  error: string | null;
  capture: () => Promise<File | null>;
}

const CAMERA_ERROR_MESSAGE =
  "Não foi possível acessar a câmera. Verifique as permissões do navegador.";

const useCamera = (active: boolean): UseCameraResult => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let stream: MediaStream | null = null;

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        setReady(true);
        setError(null);
      } catch {
        if (!cancelled) {
          setError(CAMERA_ERROR_MESSAGE);
          setReady(false);
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setReady(false);
    };
  }, [active]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream || video.srcObject === stream) return;

    video.srcObject = stream;
    void video.play().catch(() => undefined);
  });

  const capture = async (): Promise<File | null> => {
    const video = videoRef.current;
    if (!video || !ready) return null;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return null;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );
    if (!blob) return null;

    return new File([blob], "captura.jpg", { type: "image/jpeg" });
  };

  return { videoRef, ready, error, capture };
};

export default useCamera;
